import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UploadCloud, X, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES_OPTIONS = [
  "Boat Modification",
  "Kit Boat Build",
  "Boat Maintenance",
  "Toolbox",
  "Canopy",
  "Trailer",
  "Caravan Accessories",
  "Something else",
];

const inputCls =
  "w-full bg-white border border-slate-300 px-4 py-3.5 text-sm text-[#343a40] placeholder:text-slate-400 outline-none focus:border-[#007bff] focus:ring-2 focus:ring-[#007bff]/20 transition-[border-color,box-shadow] duration-200";

export default function QuoteForm() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Photo must be under 4MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPhoto({ data: reader.result, name: file.name });
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, {
        ...form,
        photo: photo?.data || null,
        photo_name: photo?.name || null,
      });
      setSent(true);
      toast.success("Quote request sent — Paul will be in touch shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong — please call 0415 464 900 instead.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div
        data-testid="quote-success-panel"
        className="bg-white p-12 flex flex-col items-start gap-5 h-full justify-center min-h-[420px]"
      >
        <CheckCircle2 size={44} className="text-[#007bff]" strokeWidth={1.5} />
        <h3 className="font-display font-black uppercase tracking-tight text-3xl text-[#003366]">
          Thank you so much!
        </h3>
        <p className="text-slate-600 text-base leading-relaxed max-w-md">
          Your quote request is in, {form.name.split(" ")[0]}. Paul will be in contact with you
          ASAP — expect to hear back within the next 48 hours. For anything urgent, ring 0415 464 900.
        </p>
        <button
          onClick={() => {
            setSent(false);
            setForm({ name: "", phone: "", email: "", service: "", message: "" });
            setPhoto(null);
            setPhotoPreview(null);
          }}
          data-testid="quote-send-another-button"
          className="mt-2 border border-[#343a40] text-[#343a40] px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] hover:border-[#007bff] hover:text-[#007bff] transition-colors duration-200"
        >
          Send another request
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="quote-form" className="bg-white p-8 md:p-10 grid gap-5">
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label htmlFor="q-name" className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 block mb-2">Name *</label>
          <input id="q-name" data-testid="quote-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
        </div>
        <div>
          <label htmlFor="q-phone" className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 block mb-2">Phone *</label>
          <input id="q-phone" data-testid="quote-phone-input" required value={form.phone} onChange={set("phone")} placeholder="04XX XXX XXX" className={inputCls} />
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label htmlFor="q-email" className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 block mb-2">Email *</label>
          <input id="q-email" type="email" data-testid="quote-email-input" required value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
        </div>
        <div>
          <label htmlFor="q-service" className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 block mb-2">Service *</label>
          <select id="q-service" data-testid="quote-service-select" required value={form.service} onChange={set("service")} className={inputCls}>
            <option value="" disabled>Select a service</option>
            {SERVICES_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="q-message" className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 block mb-2">Job details *</label>
        <textarea id="q-message" data-testid="quote-message-input" required rows={4} value={form.message} onChange={set("message")} placeholder="e.g. Quintrex 4.2m — want a casting deck, rod holders and a bigger fuel tank…" className={`${inputCls} resize-none`} />
      </div>
      <div>
        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 block mb-2">Photo (optional)</span>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid="quote-photo-input"
          onChange={(e) => onFile(e.target.files?.[0])}
        />
        {photoPreview ? (
          <div data-testid="quote-photo-preview" className="flex items-center gap-4 border border-slate-300 p-3">
            <img src={photoPreview} alt="Upload preview" className="h-16 w-16 object-cover border border-slate-200" />
            <span className="text-sm text-slate-600 truncate flex-1">{photo.name}</span>
            <button
              type="button"
              data-testid="quote-photo-remove-button"
              onClick={() => { setPhoto(null); setPhotoPreview(null); fileRef.current.value = ""; }}
              className="p-2 text-slate-400 hover:text-red-500 transition-colors duration-200"
            >
              <X size={18} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            data-testid="quote-photo-dropzone"
            onClick={() => fileRef.current.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
            className="w-full border border-dashed border-slate-300 hover:border-[#007bff] hover:bg-[#007bff]/5 px-6 py-8 flex flex-col items-center gap-2 text-slate-500 hover:text-[#007bff] transition-[border-color,color,background-color] duration-200"
          >
            <UploadCloud size={26} strokeWidth={1.5} />
            <span className="text-sm font-semibold">Drop a photo here, or click to browse</span>
            <span className="text-xs text-slate-400">JPG / PNG up to 4MB — helps us quote faster</span>
          </button>
        )}
      </div>
      <button
        type="submit"
        disabled={sending}
        data-testid="quote-submit-button"
        className="mt-2 bg-[#343a40] text-white px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-[#007bff] hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100 transition-[background-color,transform,opacity] duration-200 flex items-center justify-center gap-3"
      >
        {sending && <Loader2 size={16} className="animate-spin" />}
        {sending ? "Sending…" : "Send Quote Request"}
      </button>
    </form>
  );
}
